import { Box, Button, ButtonProps, Grid, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  DataGridPremium,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridSortDirection,
  Toolbar,
} from "@mui/x-data-grid-premium";
import SearchParams, {
  OrderDirection,
  defaultParams,
} from "../../types/searchParams";
import { GetApp, Save, Settings } from "@mui/icons-material";
import { useDispatch, useSelector } from "../../store";
import { jobReportExcel, jobReportPdf } from "../../slices/report";

interface Props {
  columns: GridColDef[];
  rows: any[];
  allowExport?: boolean;
  page?: number;
  pageSize?: number;
  totalRows?: number;
  changePage?: (pageNumber?: number, pageSize?: number) => void;
  loading?: boolean;
  onCellClick?: (params: any) => void;
  getCellClassName?: (params: GridCellParams<any, any, any>) => string;
  handleSort?: (orderBy?: string, orderDirection?: OrderDirection) => void;
  highlightId?: number;
  columnVisibility?: GridColumnVisibilityModel;
  handleColumnVisibilityChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails,
  ) => void;
  filterModel?: GridFilterModel;
  handleFilterModelChange?: (
    model: GridFilterModel,
    details: GridCallbackDetails<"filter">,
  ) => void;
  handleFilterManage?: () => void;
  handleSaveFilterSet?: () => void;
  filters?: SearchParams;
  reportStart?: Date;
  reportEnd?: Date;
}

const DataGridTable: React.FC<Props> = (props) => {
  const {
    columns,
    rows,
    allowExport,
    page,
    pageSize,
    totalRows,
    changePage,
    loading,
    onCellClick,
    getCellClassName,
    handleSort,
    highlightId,
    columnVisibility,
    handleColumnVisibilityChange,
    filterModel,
    handleFilterModelChange,
    handleFilterManage,
    handleSaveFilterSet,
    filters,
    reportStart,
    reportEnd,
  } = props;
  const dispatch = useDispatch();
  const { exportStatus } = useSelector((store) => store.report);
  const theme = useTheme();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: (page ?? defaultParams.pageNumber ?? 1) - 1,
    pageSize: pageSize ?? defaultParams.pageSize ?? rows.length,
  });

  useEffect(() => {
    setPaginationModel({
      page: (page ?? defaultParams.pageNumber ?? 1) - 1,
      pageSize: pageSize ?? defaultParams.pageSize ?? rows.length,
    });
  }, [page, pageSize]);

  const exportButtonBaseProps: ButtonProps = {
    color: "primary",
    size: "small",
    startIcon: <GetApp />,
  };

  const [exportType, setExportType] = useState<"excel" | "pdf" | null>(null);

  const CustomToolbar = () => {
    const printExcel = () => {
      if (filters) {
        setExportType("excel");
        dispatch(
          jobReportExcel({
            search: filters,
            start: reportStart,
            end: reportEnd,
          }),
        ).finally(() => setExportType(null));
      }
    };
    const print = () => {
      if (filters) {
        setExportType("pdf");
        dispatch(
          jobReportPdf({
            search: filters,
            start: reportStart,
            end: reportEnd,
          }),
        ).finally(() => setExportType(null));
      }
    };
    return (
      <Toolbar style={{ justifyContent: "flex-start" }}>
        <Grid sx={{ mt: "8px" }}>
          {filterModel && handleFilterModelChange && (
            <Grid container>
              {/* <FilterPanelTrigger
                render={
                  <ToolbarButton
                    render={<Button startIcon={<FilterAlt />}>Filter</Button>}
                  />
                }
              /> */}
              {handleFilterManage && (
                <Button
                  color="primary"
                  size="small"
                  startIcon={<Settings />}
                  onClick={handleFilterManage}
                >
                  Manage Filter Sets
                </Button>
              )}
              {handleSaveFilterSet && (
                <Button
                  color="primary"
                  size="small"
                  startIcon={<Save />}
                  onClick={handleSaveFilterSet}
                >
                  Save Current Filter Set
                </Button>
              )}
            </Grid>
          )}
        </Grid>
        {allowExport && (
          <Grid container direction={"row"} alignItems={"center"} spacing={2}>
            <Grid sx={{ mt: "8px" }}>
              <Button
                {...exportButtonBaseProps}
                onClick={printExcel}
                disabled={exportStatus === "loading"}
              >
                {exportType === "excel" ? "Generating..." : "Download as Excel"}
              </Button>
            </Grid>
            <Grid sx={{ mt: "8px" }}>
              <Button
                {...exportButtonBaseProps}
                onClick={print}
                disabled={exportStatus === "loading"}
              >
                {exportType === "pdf" ? "Generating..." : "Download as PDF"}
              </Button>
            </Grid>
          </Grid>
        )}
      </Toolbar>
    );
  };

  const getOrderDirection = (direction: GridSortDirection) => {
    switch (direction) {
      case "asc":
        return "Ascending";
      case "desc":
        return "Descending";
      default:
        return undefined;
    }
  };

  const onPageChange = (model: GridPaginationModel) => {
    changePage?.(model.page + 1, model.pageSize);
    setPaginationModel({ page: model.page, pageSize: model.pageSize });
  };

  return (
    <Box
      sx={{
        width: "100%",
        textAlign: "center",
        "& .odd": {
          backgroundColor: theme.palette.background.default,
        },
        "& .even": {
          backgroundColor: theme.palette.background.paper,
        },
        "& .highlight": {
          backgroundColor: theme.palette.text.primary,
          color: theme.palette.secondary.contrastText,
        },
        "& .MuiDataGrid-menuIconButton": {
          color: "white",
        },
        "& .MuiDataGrid-sortIcon": {
          color: "white",
        },
        "& .MuiDataGrid-toolbarContainer": {
          backgroundColor: theme.palette.background.paper,
        },
        "& .MuiDataGrid-footerContainer": {
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DataGridPremium
        columns={columns}
        rows={rows}
        pagination
        rowCount={totalRows ?? rows.length}
        loading={!!loading}
        paginationMode={changePage ? "server" : "client"}
        pageSizeOptions={[
          5,
          10,
          { label: "All", value: totalRows ? totalRows : rows.length },
        ]}
        paginationModel={paginationModel}
        onPaginationModelChange={onPageChange}
        getRowClassName={(params) =>
          params.id === highlightId
            ? "highlight"
            : params.indexRelativeToCurrentPage % 2 === 0
            ? "even"
            : "odd"
        }
        onCellClick={onCellClick}
        getRowHeight={() => "auto"}
        initialState={{
          columns: { columnVisibilityModel: columnVisibility },
        }}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        slotProps={{
          cell: {
            style: {
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              textAlign: "center",
            },
          },
          filterPanel: {
            logicOperators: [GridLogicOperator.And],
          },
          loadingOverlay: {
            variant: "circular-progress",
            noRowsVariant: "circular-progress",
          },
        }}
        sortingMode={handleSort ? "server" : "client"}
        onSortModelChange={(model) => {
          if (model.length > 0) {
            handleSort?.(model[0].field, getOrderDirection(model[0].sort));
          } else {
            handleSort?.(undefined, undefined);
          }
        }}
        getCellClassName={getCellClassName}
        filterMode={
          !!filterModel && !!handleFilterModelChange ? "server" : "client"
        }
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        slots={{
          toolbar:
            allowExport || (!!filterModel && !!handleFilterModelChange)
              ? CustomToolbar
              : undefined,
          noRowsOverlay: () => (
            <Grid
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
              container
              rowSpacing={0}
            >
              {filters?.query && (
                <Grid>
                  {`No results for search query: ${
                    filters?.query ? `'${filters?.query}'` : ""
                  }`}
                </Grid>
              )}
              {!!filters?.filters?.length && (
                <Grid>
                  {`No results for filters: ${filters?.filters
                    ?.map((f) => f.split(":")[0])
                    .join(", ")}`}
                </Grid>
              )}
              {!filters?.query && !filters?.filters?.length && (
                <Grid>{`No results`}</Grid>
              )}
            </Grid>
          ),
        }}
        showToolbar={
          allowExport || (!!filterModel && !!handleFilterModelChange)
        }
        // hideFooter={allowExport ? false : (totalRows ?? 0) <= (pageSize ?? 0)}
        disableRowSelectionOnClick
        disableRowGrouping
        disableColumnPinning
        disableAggregation
        disablePivoting
        disableColumnResize
        sx={{
          "@media print": {
            ".MuiDataGrid-main": {
              width: "fit-content",
            },
          },
          "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
            opacity: 1,
            visibility: "visible",
            width: "auto",
          },
        }}
      />
    </Box>
  );
};

export default DataGridTable;
